import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { BrandTicket, buildDraftFileUrl } from '../../api/payment/brand-deal.api';

/** Lớp dấu tên người đặt mờ, lặp chéo phủ lên phần chữ (tách từ màn duyệt bài để đề xuất dùng lại). */
export const WatermarkOverlay: React.FC<{ text: string }> = ({ text }) => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden select-none">
    <div className="flex origin-top-left flex-wrap content-start gap-x-8 gap-y-6 rotate-[-18deg] scale-125 p-2 opacity-[0.12]">
      {Array.from({ length: 36 }).map((_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <span key={i} className="whitespace-nowrap text-[11px] font-bold uppercase tracking-wide text-slate-500 dark:text-slate-300">
          {text}
        </span>
      ))}
    </div>
  </div>
);

/**
 * Một ảnh/video bản ĐÃ ĐÓNG DẤU xem qua vé ngắn hạn (5 phút): tự xin vé mới khi hết hạn hoặc phần tử media lỗi (còn hạn mà vẫn lỗi ⇒ chịu thua,
 * không lặp vô hạn). Chặn chuột phải, chặn kéo, video không có nút tải. `loadTicket` phải ổn định (useCallback) ở nơi gọi.
 */
const BrandWatermarkedMedia: React.FC<{ kind: 'image' | 'video'; loadTicket: () => Promise<BrandTicket> }> = ({ kind, loadTicket }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [broken, setBroken] = useState(false);
  const expiresAtRef = useRef(0);

  const fetchTicket = useCallback(async () => {
    try {
      const ticket = await loadTicket();
      expiresAtRef.current = Date.parse(ticket.expiresAt) || 0;
      setUrl(buildDraftFileUrl(ticket.url));
      setBroken(false);
    } catch {
      setBroken(true);
    }
  }, [loadTicket]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  const handleError = () => {
    if (Date.now() >= expiresAtRef.current) fetchTicket();
    else setBroken(true);
  };

  if (broken || !url) {
    return (
      <div className="flex h-36 w-full items-center justify-center rounded-lg border ui-divider bg-slate-100 text-[11px] ui-muted dark:bg-slate-800">
        {broken ? 'Không tải được' : <Loader2 className="animate-spin" size={16} />}
      </div>
    );
  }

  if (kind === 'video') {
    return (
      // eslint-disable-next-line jsx-a11y/media-has-caption
      <video
        src={url}
        className="h-36 w-full rounded-lg bg-black object-contain"
        controls
        controlsList="nodownload"
        disablePictureInPicture
        playsInline
        onContextMenu={(e) => e.preventDefault()}
        onError={handleError}
      />
    );
  }

  return (
    <img
      src={url}
      alt=""
      draggable={false}
      className="h-36 w-full select-none rounded-lg border ui-divider object-contain"
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      onError={handleError}
    />
  );
};

export default BrandWatermarkedMedia;
